import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import PasswordStrength from "../components/PasswordStrength";

import { API_BASE } from "../utils/api";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [userData, setUserData] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenError("Aucun token fourni");
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/auth/verify-reset-token?token=${token}`);
        
        if (response.ok) {
          const data = await response.json();
          setTokenValid(true);
          setUserData(data);
        } else {
          const error = await response.json();
          setTokenError(error.detail || "Lien invalide ou expiré");
        }
      } catch (error) {
        setTokenError("Erreur de connexion au serveur");
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password strength
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(newPassword);
    
    if (newPassword.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    
    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecial) {
      toast.error("Le mot de passe doit contenir une majuscule, une minuscule, un chiffre et un caractère spécial");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          new_password: newPassword
        }),
      });

      if (response.ok) {
        setSuccess(true);
        toast.success("Mot de passe créé avec succès !");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la création du mot de passe");
      }
    } catch (error) {
      toast.error("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#101E4D] animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#101E4D] mb-2">
              {userData?.is_first_login ? "Bienvenue dans FLOW !" : "Mot de passe mis à jour !"}
            </h2>
            <p className="text-slate-600 mb-6">
              {userData?.is_first_login 
                ? "Ton compte est prêt. Tu peux maintenant accéder à ton parcours."
                : "Tu peux maintenant te connecter avec ton nouveau mot de passe."
              }
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white rounded-full px-8"
              data-testid="go-to-login-btn"
            >
              Accéder à FLOW
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#101E4D] mb-2">Lien invalide</h2>
            <p className="text-slate-600 mb-6">{tokenError}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/forgot-password")}
                className="w-full bg-[#101E4D] hover:bg-[#0A1330] text-white rounded-full"
              >
                Demander un nouveau lien
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/login")}
                className="w-full rounded-full"
              >
                Retour à la connexion
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="text-3xl font-black text-[#101E4D]">FLOW</div>
          </div>
          <CardTitle className="text-xl text-[#101E4D]">
            {userData?.is_first_login ? "Crée ton mot de passe" : "Nouveau mot de passe"}
          </CardTitle>
          <CardDescription>
            {userData?.is_first_login 
              ? `Bienvenue ${userData?.user_name} ! Choisis un mot de passe pour accéder à ton parcours.`
              : "Choisis un nouveau mot de passe sécurisé."
            }
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">
                {userData?.is_first_login ? "Ton mot de passe" : "Nouveau mot de passe"}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D] rounded-xl"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <PasswordStrength password={newPassword} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D] rounded-xl"
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
              className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] text-white h-12 rounded-full text-base font-semibold"
              data-testid="reset-password-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                userData?.is_first_login ? "Créer mon mot de passe" : "Mettre à jour"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/login")}
              className="text-sm text-slate-500 hover:text-[#101E4D] flex items-center justify-center mx-auto"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Retour à la connexion
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
